import { cn } from '@forest/shared'
import { useStore } from '@nanostores/react'
import { useRive } from '@rive-app/react-canvas'
import { AnimatePresence } from 'motion/react'
import { useState } from 'react'
import rivGeneral from '~/assets/rive/general.riv'
import { getLocalizedMessage } from '~/lib/l10n'
import { motion } from '~/lib/motion'
import { $config } from '~/store/config'
import { BottomFinalRewardFrame } from './graphics/bottom-final-reward-frame'
import { FinalRewardFrameGeneral } from './graphics/final-reward-frame-general'
import { FinalRewardFramePaid } from './graphics/final-reward-frame-paid'
import { Spinner } from '@forest/shared/react'

export function FixedFinalRewards(props: {
  finalCheckpoint: number
  paidIconUrl: string
  freeIconUrl: string
  paidTitleKey: string | null
  freeTitleKey: string | null
  paidStatus: 'pending' | 'claimable' | 'claimed'
  paidLocked: boolean
  freeStatus: 'pending' | 'claimable' | 'claimed'
  openPreview: (type: 'free' | 'paid') => void
}) {
  const config = useStore($config)
  const [isPaidImgLoaded, setIsPaidImgLoaded] = useState(false)
  const [isFreeImgLoaded, setIsFreeImgLoaded] = useState(false)
  const paidTitle = getLocalizedMessage(props.paidTitleKey)
  const freeTitle = getLocalizedMessage(props.freeTitleKey)
  if (!config) return null

  const isFinalCheckpointDone = props.freeStatus !== 'pending'

  return (
    <>
      <BottomFinalRewardFrame colors={config.final_reward_footer_background} />
      <div className="flex h-[156px] items-start justify-center pt-4">
        {/* Paid track */}
        <div className="flex w-[115px] shrink-0 justify-center">
          <div
            className="relative size-[104px]"
            onClick={() => {
              props.openPreview('paid')
            }}
          >
            <FinalRewardFramePaid
              colors={config.paid_final_reward_background}
            />
            <motion.div
              className={cn(
                'absolute left-1/2 top-[6px] size-20 -translate-x-1/2',
                (props.paidLocked || props.paidStatus === 'claimed') &&
                  'opacity-30',
              )}
              initial={{ opacity: 0 }}
              animate={{
                opacity: isPaidImgLoaded
                  ? props.paidLocked || props.paidStatus === 'claimed'
                    ? 0.3
                    : 1
                  : 0,
              }}
            >
              <img
                src={props.paidIconUrl}
                onLoad={() => setIsPaidImgLoaded(true)}
                loading="lazy"
              />
            </motion.div>
            {props.paidStatus === 'claimed' && (
              <ClaimedCheckmark className="absolute left-1/2 top-[6px] size-20 -translate-x-1/2" />
            )}
            {props.paidLocked && <Lock />}
            <AnimatePresence initial={false}>
              {!isPaidImgLoaded && (
                <motion.div
                  key="paid-img-spinner"
                  exit={{ opacity: 0 }}
                  className="absolute left-1/2 top-8 size-9 -translate-x-1/2"
                >
                  <Spinner className="size-full" />
                </motion.div>
              )}
            </AnimatePresence>
            <Badge className="absolute -bottom-2 left-1/2 -translate-x-1/2">
              {paidTitle ?? ''}
            </Badge>
          </div>
        </div>

        <div
          className={cn(
            'relative flex w-[105px] shrink-0 justify-center pt-10',
            isFinalCheckpointDone ? '' : 'opacity-30',
          )}
          style={{
            ...(!isFinalCheckpointDone && {
              mixBlendMode: 'multiply',
            }),
          }}
        >
          <div
            className="absolute bottom-2 left-0 right-0 top-[-22px] mx-auto w-[11px]"
            style={{
              background: isFinalCheckpointDone
                ? 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 10%, #fff 50%)'
                : 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 10%, #000 50%)',
              backgroundBlendMode: isFinalCheckpointDone ? '' : 'multiply',
            }}
          />
          <span
            className={cn(
              'relative grid size-9 place-items-center rounded-[50%] text-[10px] font-bold text-white',
              isFinalCheckpointDone
                ? 'bg-white text-[#333]'
                : 'bg-black text-white',
            )}
          >
            {props.finalCheckpoint}
          </span>
        </div>

        {/* Free track */}
        <div className="flex w-[115px] shrink-0 justify-center">
          <div
            className="relative size-[104px]"
            onClick={() => {
              props.openPreview('free')
            }}
          >
            <FinalRewardFrameGeneral
              colors={config.free_final_reward_background}
            />
            <motion.div
              className={cn(
                'absolute left-1/2 top-[6px] size-20 -translate-x-1/2',
                props.freeStatus === 'claimed' && 'opacity-30',
              )}
              initial={{ opacity: 0 }}
              animate={{
                opacity: isFreeImgLoaded
                  ? props.freeStatus === 'claimed'
                    ? 0.3
                    : 1
                  : 0,
              }}
            >
              <img
                src={props.freeIconUrl}
                onLoad={() => setIsFreeImgLoaded(true)}
                loading="lazy"
              />
            </motion.div>
            {props.freeStatus === 'claimed' && (
              <ClaimedCheckmark className="absolute left-1/2 top-[6px] size-20 -translate-x-1/2" />
            )}
            <AnimatePresence initial={false}>
              {!isFreeImgLoaded && (
                <motion.div
                  key="free-img-spinner"
                  exit={{ opacity: 0 }}
                  className="absolute left-1/2 top-8 size-9 -translate-x-1/2"
                >
                  <Spinner className="size-full" />
                </motion.div>
              )}
            </AnimatePresence>
            <Badge className="absolute -bottom-2 left-1/2 -translate-x-1/2">
              {freeTitle ?? ''}
            </Badge>
          </div>
        </div>
      </div>
    </>
  )
}

function ClaimedCheckmark(props: { className?: string }) {
  return (
    <div className={cn('flex items-center justify-center', props.className)}>
      <svg
        width="31"
        height="27"
        viewBox="0 0 31 27"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M26.2818 4.2572C27.7046 5.42539 27.911 7.52582 26.7428 8.94862L15.7955 22.2819C15.177 23.0352 14.2597 23.4801 13.2851 23.4993C12.3106 23.5186 11.3765 23.1103 10.7287 22.382L4.34272 15.2026C3.11922 13.827 3.24247 11.7201 4.618 10.4966C5.99354 9.27309 8.10047 9.39634 9.32398 10.7719L13.1179 15.0372L21.5904 4.7182C22.7586 3.2954 24.859 3.089 26.2818 4.2572Z"
          fill="white"
        />
        <path
          d="M20.2383 3.60742C22.0198 1.43799 25.2229 1.12291 27.3926 2.9043C29.5624 4.6858 29.8772 7.88979 28.0957 10.0596L17.1484 23.3926C16.2052 24.5414 14.8055 25.2197 13.3193 25.249C11.8333 25.2783 10.4087 24.6555 9.4209 23.5449L3.03516 16.3652C1.16956 14.2676 1.35759 11.0553 3.45508 9.18945C5.55274 7.32364 8.76598 7.51079 10.6318 9.6084L13.0645 12.3438L20.2383 3.60742Z"
          stroke="white"
          strokeOpacity="0.3"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}

function Lock() {
  const { RiveComponent } = useRive({
    src: rivGeneral,
    autoplay: true,
    stateMachines: ['State Machine 1'],
    artboard: 'lock',
    autoBind: true,
  })

  return (
    <RiveComponent className="pointer-events-none absolute left-0 top-0 h-[104px] w-[113px]" />
  )
}

function Badge(props: { children: string; className?: string }) {
  return (
    <div
      className={cn(
        'flex h-5 items-center text-nowrap rounded-3xl px-2 text-[10px] font-semibold text-[#333]',
        props.className,
      )}
      style={{
        background: 'linear-gradient(180deg, #FFF 0%, #E3E3E3 100%)',
        boxShadow: '0 0 4px 0 #FFFFFF99',
      }}
    >
      {props.children}
    </div>
  )
}
